// src/app/core/services/branding.service.ts
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, timeout } from 'rxjs';
import {
  InstitutionSettings,
  InstitutionSettingsService,
} from './institution-settings.service';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class BrandingService {
  private settingsSubject = new BehaviorSubject<InstitutionSettings | null>(null);
  settings$ = this.settingsSubject.asObservable();

  private currentSettings: InstitutionSettings | null = null;

  constructor(
    private institutionSettingsService: InstitutionSettingsService,
  ) { }

  /**
   * Se ejecuta al iniciar la app (APP_INITIALIZER).
   *
   * Estrategia:
   * 1. Pinta inmediatamente lo último conocido desde localStorage (síncrono, no bloquea).
   * 2. Si había caché, la actualización desde el backend corre en SEGUNDO PLANO
   *    (no bloquea el arranque de la app).
   * 3. Si NO había caché (primer login / navegador limpio), esperamos la respuesta
   *    del backend con un timeout corto, porque no hay nada que mostrar todavía.
   */
  async load(): Promise<void> {
    const hadCache = this.loadFromLocalStorage();
    
    if (hadCache) {
      // Ya tenemos algo pintado: actualizamos sin bloquear el bootstrap
      this.refreshFromServer();
      return;
    }

    // No hay nada en caché: esperamos la primera respuesta, pero con timeout corto
    // para no dejar la app colgada si el backend está lento/caído.
    try {
      const settings = await firstValueFrom(
        this.institutionSettingsService
          .getSettings(false)
          .pipe(timeout(3000))
      );
      
      if (settings) {
        this.setSettings(settings);
        localStorage.setItem('institution_settings', JSON.stringify(settings));
      }
    } catch (error) {
      console.warn('No fue posible cargar el branding inicial.', error);
      // La app arranca igual, sin branding personalizado (usa defaults del CSS/tema).
    }
  }

  /**
   * Actualiza el branding desde el backend en segundo plano.
   * No bloquea nada: si falla o tarda, simplemente se mantiene lo que ya
   * está pintado (venía de localStorage).
   */
  private refreshFromServer(): void {
    this.institutionSettingsService
      .getSettings(false)
      .pipe(timeout(5000))
      .subscribe({
        next: (settings) => {
          if (!settings) return;
          this.setSettings(settings);
          localStorage.setItem('institution_settings', JSON.stringify(settings));
        },
        error: (error) => {
          console.warn('No fue posible actualizar el branding.', error);
        },
      });
  }

  private setSettings(settings: InstitutionSettings): void {
    this.currentSettings = settings;
    this.settingsSubject.next(settings);
    this.applyBranding(settings);
  }

  /**
   * Carga el branding cacheado en localStorage.
   * @returns true si había datos válidos y se aplicaron, false si no había nada.
   */
  private loadFromLocalStorage(): boolean {
    const raw = localStorage.getItem('institution_settings');
    if (!raw) return false;

    try {
      const parsed = JSON.parse(raw) as InstitutionSettings;
      this.currentSettings = parsed;
      this.settingsSubject.next(parsed);
      this.applyBranding(parsed);
      return true;
    } catch (e) {
      console.error('Error parseando institution_settings de localStorage', e);
      // Caché corrupta: la limpiamos para no reintentar parsear basura en cada arranque
      localStorage.removeItem('institution_settings');
      return false;
    }
  }

  /** Devuelve la config actual de forma síncrona */
  getSettings(): InstitutionSettings | null {
    return this.currentSettings;
  }

  /** Aplica los colores al :root como CSS variables */
  private applyBranding(settings: InstitutionSettings) {
    const root = document.documentElement;

    if (settings.primaryColor) {
      root.style.setProperty('--institution-primary', settings.primaryColor);

      const themeMeta = document.querySelector('meta[name="theme-color"]');
      if (themeMeta) {
        themeMeta.setAttribute('content', settings.primaryColor);
      }
    }
  }

  /** Devuelve la URL pública del logo lista para usar en <img> */
  getLogoUrl(): string | null {
    if (!this.currentSettings?.logoUrl) return null;

    const raw = this.currentSettings.logoUrl.trim();
    // soporta "logo-xxx.png" o "branding/logo-xxx.png"
    const fileNameWithExt = raw.includes('/') ? raw.split('/').pop()! : raw;

    const parts = fileNameWithExt.split('.');
    if (parts.length < 2) {
      console.warn('logoUrl sin extensión válida:', raw);
      return null;
    }

    const ext = parts.pop()!.toLowerCase();   // "png"
    const baseName = parts.join('.');         // "logo-1764202000603"

    const allowedExt = ['png', 'jpg', 'jpeg', 'ico'];
    if (!allowedExt.includes(ext)) {
      console.warn('Extensión de logo no permitida:', ext);
      return null;
    }

    const apiBase = environment.base.replace(/\/+$/, '');

    return `${apiBase}/assets/branding/${baseName}?ext=${ext}`;
  }
}
